import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { BasicLeadForm } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { getBankName } from "@component/assets/data/banks";
import { Config } from "@config";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { http } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { searchParamsToRequest } from "../Calculator/domain";
import * as Step from "./Steps";
import { BANK_BRANCHES } from "./cibBankBranches";
import { BANK_BRANCHES_ERSTE } from "./ersteBankBranches";
import type { RetailAccountCalcRequest, RetailAccountCalcResponse } from "./types";
import "./style.scss";

export interface RetailAccountLeadFormFields extends AbstractLeadFormFields {
	question?: string;

	// Privi and Eco only
	bankBranchCounty?: string;
	bankBranch?: string;
	birthName?: string;
	birthDate?: string;
	motherName?: string;
}

export type RetailAccountLeadFormProps = AbstractLeadFormProps<RetailAccountCalcRequest, RetailAccountCalcResponse>;
export interface RetailAccountLeadFormState<
	FormFields extends RetailAccountLeadFormFields = RetailAccountLeadFormFields,
> extends AbstractLeadFormState<FormFields> {
	errorMessage?: string;
	submitStateOnline?: ProgressButtonState;
	wantsWithOnlineService?: boolean;
}

export class RetailAccountLeadForm<
	FormFields extends RetailAccountLeadFormFields = RetailAccountLeadFormFields,
	Props extends RetailAccountLeadFormProps = RetailAccountLeadFormProps,
	State extends RetailAccountLeadFormState<FormFields> = RetailAccountLeadFormState<FormFields>,
> extends AbstractLeadForm<RetailAccountCalcRequest, RetailAccountCalcResponse, FormFields, Props, State> {
	// EventParams
	protected readonly productTypes: string = "RETAIL_ACCOUNT";
	protected readonly componentType: EventComponentType = "leadform";

	public static readonly defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
	};

	protected renderSteps(): ReactNode {
		const { title } = this.props;
		const {
			values: { email, lastName, firstName },
			submitState,
		} = this.state;

		const branchData = this.getBranchData();
		const fullName = `${lastName} ${firstName}`;

		return (
			<>
				<BasicLeadForm
					stepName="lead-form"
					defaultTarget="lead-form-target"
					title={title}
					button="Visszahívást kérek"
					className="pb-3"
					submitState={submitState}
					crossSellOption
					newsletterOption
				/>
				<Step.ExtraData
					stepName="extra-data"
					defaultTarget="branch-select"
					fullName={fullName}
					submitState={submitState}
				/>
				<Step.BranchSelect
					stepName="branch-select"
					defaultTarget="success-page"
					bankBranches={branchData}
					submitState={submitState}
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "lead-form-target":
				if (this.isEco() || this.isErstePrivi()) {
					return this.submitLead("extra-data");
				}

				return this.onStep("success-page", true);
			case "extra-data":
				this.setState({ wantsWithOnlineService: this.isEco() ? false : undefined }, () =>
					this.submitLead(target),
				);
				break;
			case "success-page":
				return this.submitLead(target);
			case "branch-select":
				this.submitLead(target);
				break;

			default:
				super.onStep(target, withEvent);
		}

		return undefined;
	}

	private submitLead(target: string): void {
		const { leadId } = this.state;

		if (leadId) {
			return this.updateLeadData(target);
		}

		this.sendLead()
			.then((response) => {
				this.setState({
					submitState: undefined,
					leadId: response.leadId,
				});

				super.onStep(target, false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	private updateLeadData(target: string, branchId?: number): void {
		const {
			values: { birthName, birthDate, motherName, bankBranch, email },

			wantsWithOnlineService,
			leadId,
		} = this.state;

		this.setState({
			submitState: "loading",
		});

		const otherData = new JsonDataBuilder();
		otherData.addItem("Születési idő", "birthDate", birthDate);
		otherData.addItem("Születési név", "birthName", birthName);
		otherData.addItem("Anyja leánykori neve", "motherName", motherName);
		otherData.addItem("Bankfiók", "bankBranch", bankBranch);

		const updateData = {
			leadData: {
				branch: branchId,
				other_data_json: bankBranch ? otherData.getObject() : undefined,
				data_lock: bankBranch ? "DATA_FORM_LOCK" : undefined,
				wants_with_online_service: wantsWithOnlineService,
			},
			customerData: birthDate
				? {
						birth_date: birthDate,
						mother_name: motherName,
						birth_name: birthName,
					}
				: undefined,
		};

		this.promisePool
			.add(
				http.put(
					Config.getWpRestApiUrl(`/myfinances/lead-and-customer/${encodeURIComponent(leadId)}`, {
						email: email,
					}),
					updateData,
				),
			)
			.then(() => {
				this.setState({
					submitState: undefined,
				});

				super.onStep(target, true);
			})
			.catch((error: Error) => {
				this.setState({
					submitState: "error",
				});
				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	protected getLeadData(): LeadRequest<RetailAccountCalcRequest, RetailAccountCalcResponse> {
		const {
			values: { question },
			wantsWithOnlineService,
		} = this.state;
		const data = super.getLeadData();

		data.wantsWithOnlineService = wantsWithOnlineService;
		data.request.question = question;

		return data;
	}

	protected getProductName(): string {
		const { response } = this.props;

		return response?.productName;
	}

	protected getBankCode(): string {
		const { response } = this.props;

		return response?.bankCode;
	}

	protected isEco(): boolean {
		return this.getProductName()?.includes("ECO");
	}

	private isErstePrivi(): boolean {
		return this.getProductName()?.includes("Erste Privilégium 2.0");
	}

	private getBranchData(): Record<string, Record<string, string>> {
		if (this.isEco()) {
			return BANK_BRANCHES;
		}

		if (this.isErstePrivi()) {
			return BANK_BRANCHES_ERSTE;
		}

		return {};
	}

	protected getCalculationData(): string {
		const { searchParams, request } = this.props;

		if (searchParams === undefined) {
			return null;
		}

		const data = searchParamsToRequest(
			{
				...request,
				...searchParams,
			},
			searchParams?.sort,
		);

		return JSON.stringify({
			...data,
			bankName: getBankName(data.bank),
		});
	}

	protected getAutoresponderName(): string {
		return undefined;
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName, email },
		} = this.state;

		return {
			firstName,
			lastName,
			encodedFirstName: encodeURIComponent(firstName),
			encodedLastName: encodeURIComponent(lastName),
			email,
		};
	}

	protected getProductType(): ProductType {
		return "RETAIL_ACCOUNT";
	}

	protected getClassName(): string {
		return `${super.getClassName()} retail-account`;
	}

	protected getComponentName(): string {
		return "RetailAccountLeadForm";
	}
}

export default RetailAccountLeadForm;
